import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Input, Space, Typography, message } from 'antd';
import { UnlockOutlined, ClockCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useAppDispatch, useAppSelector } from '../../../Modules/Presentation/hooks';
import { selectUser } from '../../../Modules/User/Presentation/selectors';
import {
  canUseSlotOpsOverride,
  isLimitedPartnerAdmin,
  isSlotOpsOverrideActive,
} from '../../../Utils/limitedPartnerAdmin';
import { isLimitedAdminInRestrictedHours } from '../../../Utils/timeRestriction';
import {
  clearSlotOpsOverrideThunk,
  refreshMeThunk,
  slotOpsOverrideThunk,
} from '../../../Modules/User/Presentation/actions';

const { Text } = Typography;

function formatRemaining(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

const SlotOpsOverrideBanner: React.FC = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [remainingSec, setRemainingSec] = useState(0);
  const [, setClockTick] = useState(0);

  const eligible = canUseSlotOpsOverride(user);
  const active = isSlotOpsOverrideActive(user);
  const inRestrictedHours = isLimitedAdminInRestrictedHours(user);
  const expiresAt = user?.slot_ops_override?.expires_at;

  const syncRemaining = useCallback(() => {
    if (!active || !expiresAt) {
      setRemainingSec(0);
      return;
    }
    const diff = dayjs(expiresAt).diff(dayjs(), 'second');
    if (diff <= 0) {
      setRemainingSec(0);
      dispatch(clearSlotOpsOverrideThunk());
      return;
    }
    setRemainingSec(diff);
  }, [active, dispatch, expiresAt]);

  useEffect(() => {
    syncRemaining();
    if (!active) return undefined;
    const timer = window.setInterval(syncRemaining, 1000);
    return () => window.clearInterval(timer);
  }, [active, syncRemaining]);

  useEffect(() => {
    if (!eligible) return undefined;
    const timer = window.setInterval(() => {
      setClockTick(t => t + 1);
      dispatch(refreshMeThunk());
    }, 30_000);
    return () => window.clearInterval(timer);
  }, [dispatch, eligible]);

  useEffect(() => {
    if (!eligible) return undefined;
    const timer = window.setInterval(() => setClockTick(t => t + 1), 60_000);
    return () => window.clearInterval(timer);
  }, [eligible]);

  if (!isLimitedPartnerAdmin(user) || !eligible) {
    return null;
  }

  const handleActivate = async () => {
    if (!password.trim()) {
      message.warning('슈퍼어드민 비밀번호를 입력해주세요.');
      return;
    }
    setLoading(true);
    try {
      await dispatch(slotOpsOverrideThunk(password.trim())).unwrap();
      setPassword('');
      message.success('15분간 슬롯 작업 시간 제한이 해제되었습니다.');
    } catch {
      message.error('비밀번호가 올바르지 않거나 임시 해제에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  if (active && remainingSec > 0) {
    return (
      <Alert
        type="success"
        showIcon
        icon={<ClockCircleOutlined />}
        message={
          <Space wrap>
            <Text strong>임시 해제 중</Text>
            <Text>{formatRemaining(remainingSec)} 남음</Text>
            <Text type="secondary">(시작일·종료일 변경, 공지/로그 삭제 제한은 그대로 적용됩니다)</Text>
          </Space>
        }
        style={{ marginBottom: 16 }}
      />
    );
  }

  if (!inRestrictedHours) {
    return null;
  }

  return (
    <Alert
      type="warning"
      showIcon
      icon={<UnlockOutlined />}
      message="제한형 관리자 — 슬롯 작업 시간 제한"
      description={
        <Space direction="vertical" style={{ width: '100%' }} size="small">
          <Text>
            평일 17:30 이후·주말 15:00 이후에는 슬롯 작업이 제한됩니다. 제한 시간 이후에는 슈퍼어드민 비밀번호로 15분간 임시 해제할 수 있습니다.
          </Text>
          <Space.Compact style={{ maxWidth: 420, width: '100%' }}>
            <Input.Password
              placeholder="임시 해제 비밀번호"
              value={password}
              onChange={e => setPassword(e.target.value)}
              onPressEnter={handleActivate}
            />
            <Button type="primary" loading={loading} onClick={handleActivate}>
              임시 해제
            </Button>
          </Space.Compact>
        </Space>
      }
      style={{ marginBottom: 16 }}
    />
  );
};

export default SlotOpsOverrideBanner;
